import React, { ReactNode, useEffect, useState } from 'react';
import { toast } from 'react-toastify';

type NXFormProps = {
    title?: string;
    onSubmit: (e: React.FormEvent) => void;
    className?: string;
    nonFieldError?: any;
    children: ReactNode;
    showErrorPopup?: boolean;
    toastError?: boolean;
    allButSkipAllLogic?: boolean;
};

const NXForm: React.FC<NXFormProps> = ({ title, onSubmit, className, nonFieldError, showErrorPopup = false, toastError=false, children, allButSkipAllLogic=false }) => {
    // Extract error messages from the nonFieldError
    const errorMessages: string[] = [];

    if (!allButSkipAllLogic && nonFieldError) {
        if (Array.isArray(nonFieldError.error)) {
            errorMessages.push(...nonFieldError.error);
        } else if (nonFieldError.error?.details) {
            errorMessages.push(nonFieldError.error.details);
        }

        if (Array.isArray(nonFieldError.non_field_errors)) {
            errorMessages.push(...nonFieldError.non_field_errors);
        }

        if (Array.isArray(nonFieldError)) {
            errorMessages.push(...nonFieldError);
        }

        if (nonFieldError instanceof Object && Object.keys(nonFieldError).length > 0) {
            errorMessages.push(nonFieldError.detail || nonFieldError.details || nonFieldError.error);
        }
    } else if (allButSkipAllLogic && nonFieldError) {
        // convert all object values to array and an object value can be array of error or array of object with more error key message. all message put in array

        const errorArray = Object.values(nonFieldError);
        errorArray.forEach((error: any) => {
            if (Array.isArray(error)) {
                errorMessages.push(...error);
            } else if (error instanceof Object) {
                errorMessages.push(...Object.values(error).map(String));
            } else {
                errorMessages.push(error);
            }
        });
    }

    // State for managing popup visibility
    const [popupVisible, setPopupVisible] = useState(true);

    // Function to dismiss the popup
    const dismissPopup = () => {
        setPopupVisible(false);
    };

    // Effect to show the popup when new errors are detected
    useEffect(() => {
        if (errorMessages.length > 0) {
            setPopupVisible(true);
        }
    }, [nonFieldError]); // Runs whenever nonFieldError changes

    return (
        <form className={className} onSubmit={onSubmit}>
            {title && (
                <h2 className="text-lg font-medium text-gray-900">
                    {title}
                </h2>
            )}

            {/* Render error messages in a dismissible popup */}
            {showErrorPopup && popupVisible && errorMessages.length > 0 && (
                <>
                    {/* Backdrop for disabling interaction */}
                    <div 
                        className="fixed inset-0 z-40" 
                        onClick={dismissPopup} // Clicking the backdrop dismisses the popup
                    />
                    <div className="relative fixed top-0 right-0 m-4 bg-red-200 border border-red-400 text-red-700 p-4 rounded shadow-lg z-50">
                        <button
                            className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center rounded-full bg-red-400 text-white hover:bg-red-500 focus:outline-none transition duration-200 transform translate-x-1/2 -translate-y-1/2"
                            onClick={dismissPopup}
                            aria-label="Dismiss error message"
                        >
                            <span className="text-xl">&times;</span>
                        </button>
                        {errorMessages.map((error: any, index: number) => (
                            <p key={index}>{error}</p>
                        ))}
                    </div>
                </>
            )}

            {/* Render inline error messages if showErrorPopup is false */}
            {!showErrorPopup && !toastError && errorMessages.length > 0 && (
                <div className="text-red-600 p-2">
                    {errorMessages.map((error: any, index: number) => (
                        <p key={index}>{error}</p>
                    ))}
                </div>
            )}

            {/* Render error via toast */}
            {toastError && errorMessages.length > 0 && (
                errorMessages.every(error => typeof error === 'string') 
                ? toast.error(errorMessages.join("\n"))
                : toast.error("An error occurred")
            )}

            {children}
        </form>
    );
};

export default NXForm;
